import { useNavigate } from 'react-router-dom';

import { AgencyType } from '@/models/agency';
import { IUser, Role } from '@/models/user';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';

import { CommandDefinition } from '../types';

// Type definitions for page code metadata
interface PageCode {
  code: string;
  label: string;
  path: string;
  requiredRoles?: Role[];
  requiredAgencies?: AgencyType[];
}

/**
 * Check if a user has permission to access a specific page
 * @param page The page to check permission for
 * @param user The user for role checking
 * @returns boolean indicating if the user has permission
 */
const hasPagePermission = (page: PageCode, user: IUser): boolean => {
  // If no restrictions defined, allow access
  if (!page.requiredRoles?.length && !page.requiredAgencies?.length) {
    return true;
  }

  // If user or roles are missing, deny access to restricted pages
  if (!user?.roles?.length) {
    return false;
  }

  // Check if user has any of the required roles
  if (page.requiredRoles?.length) {
    return page.requiredRoles.some((requiredRole) =>
      user.roles.some((userRole) => userRole.role === requiredRole),
    );
  }

  // If no role restrictions but has agency restrictions, allow for now
  // TODO: Implement agency checking when needed
  return true;
};

// Page codes mapping with explicit permission requirements
const PAGE_CODES: PageCode[] = [
  {
    code: 'mycfs',
    label: 'My CFS',
    path: '/my-cfs',
    requiredRoles: [Role.FIRST_RESPONDER],
  },
  {
    code: 'mapview',
    label: 'Map View',
    path: '/map-view',
    requiredRoles: [Role.FIRST_RESPONDER],
  },
  {
    code: 'admin',
    label: 'Admin',
    path: '/admin/agencies',
    requiredRoles: [Role.ACCOUNT_ADMIN, Role.AGENCY_ADMIN],
  },
  {
    code: 'ncic',
    label: 'NCIC Search',
    path: '/ncic-search',
    requiredRoles: [Role.RMS_CASES_FULL_ACCESS, Role.RMS_CASES_VIEW],
    requiredAgencies: [AgencyType.POLICE],
  },
  {
    code: 'jmsmvmnt',
    label: 'JMS Inmates movement',
    path: '/jms/operations/inmates-movement',
    requiredRoles: [Role.JAIL_OFFICER],
  },
  {
    code: 'jmsmngmnt',
    label: 'JMS Management',
    path: '/jms/management',
    requiredRoles: [Role.JAIL_OFFICER],
  },
  {
    code: 'wanted',
    label: 'Wanted Person',
    path: '/tools/wanted-person',
    requiredRoles: [
      Role.WANTED_PERSON_EDIT,
      Role.WANTED_PERSON_VIEW,
      Role.WANTED_PERSON_VIEW_ADD_ATTEMPT,
    ],
  },
  {
    code: 'locationmarkers',
    label: 'Location Markers',
    path: '/tools/location-markers',
  },
  {
    code: 'tools',
    label: 'Tools',
    path: '/tools',
  },
  // Public pages (no restrictions)
  { code: 'cfs', label: 'CFS', path: '/cfs' },
  { code: 'dispatch', label: 'Active Dispatch Requests', path: '/cfs-active' },
  { code: 'analytics', label: 'Analytics', path: '/analytics' },
  { code: 'cases', label: 'Cases', path: '/cases' },
  { code: 'evidence', label: 'Evidence', path: '/evidence' },
  { code: 'index', label: 'Master Index', path: '/master-index' },
  { code: 'tasks', label: 'Tasks', path: '/tasks' },
  { code: 'settings', label: 'Settings', path: '/settings' },
];

/**
 * Show unauthorized access toast notification
 */
const showUnauthorizedToast = (): void => {
  setTimeout(() => {
    const { updateToast } = useToastStore.getState();
    updateToast({
      message: 'You are not authorized to access this page.',
      type: ConfirmTypes.WARNING,
      open: true,
      vertical: 'top',
      horizontal: 'center',
      autoHideDuration: 4000,
    });
  }, 0);
};

export const createGoToCommand = (
  navigate: ReturnType<typeof useNavigate>,
): CommandDefinition => ({
  id: 'goto',
  label: 'Go To',
  description: 'Navigate to a specific page in the application',
  inputs: [
    {
      id: 'pageCode',
      label: 'Page',
      type: 'select',
      options: PAGE_CODES.map((page) => ({
        label: page.code,
        value: page.code,
        description: page.label,
      })),
      required: true,
      placeholder: 'Enter page code',
    },
  ],
  execute: async (values) => {
    const { pageCode } = values;
    const page = PAGE_CODES.find((p) => p.code === pageCode);

    if (!page) {
      return {
        success: false,
        message: `Page code '${pageCode}' not found`,
      };
    }

    const { user } = useUserStore.getState();
    const hasPermission = hasPagePermission(page, user as IUser);

    if (!hasPermission) {
      showUnauthorizedToast();
      return {
        success: true,
        message: `Access denied to ${page.label}`,
      };
    }

    navigate(page.path);
    return {
      success: true,
      message: `Navigating to ${page.label}`,
      data: { page },
    };
  },
});
